import React from 'react';
import { Check, MessageCircle } from 'lucide-react';

// Stable key from block content — avoids array-index-as-key warnings and
// keeps DOM identity stable if the blocks array ever changes.
const blockKey = (b, i) => {
  const seed = b.text || b.src || b.title || (b.items && b.items[0]) || '';
  return `${b.type}-${String(seed).slice(0, 40)}-${i}`;
};
const itemKey = (text, i) => `${(text || '').slice(0, 40)}-${i}`;

// Render an emoji-bullet list. Items can be plain strings (use the shared
// emoji from the parent block) or objects { emoji, text } for per-row emojis.
const EmojiList = ({ items, emoji }) => (
  <ul className="space-y-3.5 my-7 sm:my-8">
    {items.map((it, i) => {
      const text = typeof it === 'string' ? it : it.text;
      const e = typeof it === 'string' ? emoji : it.emoji || emoji;
      return (
        <li
          key={itemKey(text, i)}
          className="flex items-start gap-3.5 text-[#2A3B40] text-[16px] sm:text-[17px] leading-[1.7]"
        >
          <span className="text-lg leading-7 flex-shrink-0">{e}</span>
          <span>{text}</span>
        </li>
      );
    })}
  </ul>
);

const CheckList = ({ items }) => (
  <ul className="space-y-3.5 my-7 sm:my-8">
    {items.map((text, i) => (
      <li
        key={itemKey(text, i)}
        className="flex items-start gap-3.5 text-[#2A3B40] text-[16px] sm:text-[17px] leading-[1.7]"
      >
        <span className="mt-1 w-5 h-5 flex-shrink-0 rounded-full bg-[#0B1F26]/5 border border-[#D4AF5A]/55 flex items-center justify-center">
          <Check size={11} strokeWidth={2.5} className="text-[#0B1F26]" />
        </span>
        <span>{text}</span>
      </li>
    ))}
  </ul>
);

export const RenderBlogContent = ({ blocks }) => {
  return (
    <div data-testid="blog-content">
      {blocks.map((b, i) => {
        const k = blockKey(b, i);
        switch (b.type) {
          case 'h2':
            return (
              <h2
                key={k}
                className="text-2xl sm:text-3xl lg:text-[34px] leading-[1.2] text-[#0B1F26] mt-16 sm:mt-20 mb-6 sm:mb-7 tracking-[-0.01em]"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
              >
                {b.text}
              </h2>
            );
          case 'h3':
            return (
              <h3
                key={k}
                className="text-lg sm:text-xl text-[#0B1F26] mt-10 mb-4"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
              >
                {b.text}
              </h3>
            );
          case 'p':
            return (
              <p
                key={k}
                className="text-[16px] sm:text-[17px] leading-[1.85] text-[#2A3B40] my-5"
              >
                {b.text}
              </p>
            );
          case 'list':
            return <EmojiList key={k} items={b.items} emoji={b.emoji} />;
          case 'check':
            return <CheckList key={k} items={b.items} />;
          case 'quote':
            return (
              <blockquote
                key={k}
                className="my-10 pl-6 border-l-2 border-[#D4AF5A] italic text-[#0B1F26] text-lg sm:text-xl leading-relaxed"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {b.text}
              </blockquote>
            );
          case 'image':
            return (
              <figure key={k} className="my-12 sm:my-14 -mx-2 sm:-mx-6">
                <div className="relative aspect-[16/9] overflow-hidden rounded-2xl bg-[#E8EEEF] shadow-[0_18px_50px_-24px_rgba(11,31,38,0.35)]">
                  <img
                    src={b.src}
                    alt={b.alt || ''}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                {b.caption && (
                  <figcaption className="mt-3 text-center text-[13px] text-[#4A4A4A] italic">
                    {b.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'engagement':
            return (
              <aside
                key={k}
                className="my-14 sm:my-16 rounded-3xl border border-[#D4AF5A]/40 bg-gradient-to-br from-[#FBF6EB] via-white to-[#EFF4F5] p-7 sm:p-9 relative overflow-hidden"
              >
                <div
                  aria-hidden
                  className="absolute -top-1/2 -right-1/4 w-[45%] h-[180%] opacity-[0.08] pointer-events-none"
                  style={{
                    background: 'radial-gradient(ellipse at center, #D4AF5A 0%, transparent 60%)',
                  }}
                />
                <div className="relative">
                  <p
                    className="text-[11px] uppercase tracking-[0.5em] mb-2 flex items-center gap-2"
                    style={{ color: '#1E4A55' }}
                  >
                    <MessageCircle size={12} className="text-[#D4AF5A]" />
                    Join the conversation
                  </p>
                  <h3
                    className="text-xl sm:text-2xl text-[#0B1F26] mb-6 tracking-[-0.005em]"
                    style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                  >
                    {b.title || 'Top 3 Reader Engagement Questions'}
                  </h3>
                  <ol className="space-y-4">
                    {b.items.map((q, qi) => (
                      <li
                        key={`${q.slice(0, 30)}-${qi}`}
                        className="flex items-start gap-4 text-[#2A3B40] text-[15px] sm:text-[16px] leading-[1.7]"
                      >
                        <span
                          className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-medium"
                          style={{
                            background: 'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                            color: '#0B1F26',
                            fontFamily: 'Playfair Display, serif',
                          }}
                        >
                          {qi + 1}
                        </span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-6 text-[13px] text-[#4A4A4A] italic">
                    Share your thoughts with us on Instagram{' '}
                    <a
                      href="https://www.instagram.com/navs.art.studio/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0B1F26] hover:text-[#D4AF5A] transition-colors underline underline-offset-2"
                    >
                      @navs.art.studio
                    </a>{' '}
                    — we love hearing what art means to you.
                  </p>
                </div>
              </aside>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default RenderBlogContent;
